import React, { useEffect, useMemo, useRef } from 'react';
import { useQuestionnaires, useAttempts } from '@/Recoil/Topik';
import type { QuestionnaireType, AttemptType } from '@/Data/Topik/types';
import './index.scss';
import { navigateTo } from '@/Utils/routing';
import {CheckCircle} from '@mui/icons-material';
import { useSession } from '@/Recoil/Session/Session';

function TopikDashboard() {
    const { questionnaires, loading: questionnairesLoading, error: questionnairesError, syncQuestionnaires } = useQuestionnaires();
    const { attempts, loading: attemptsLoading, error: attemptsError, syncAttempts, createAttempt } = useAttempts();
    const {session} = useSession();

    useEffect(() => {
        // Check if user is logged in
        if (!session.email) {
            navigateTo('/login?redirectTo=/topik');
        }
    }, [session.email]);

    const init = useRef(()=>{
        syncQuestionnaires();
        syncAttempts({userid: session?.userid});
    })
    useEffect(() => {
        // because syncQuestionnaires is a function that fetches data, we want to call it only once when the component mounts
        // using useRef to avoid re-creating the function on every render
        if (session.email) {
            const debounceTimer = setTimeout(() => {
                init.current();
            }, 300);
            
            return () => clearTimeout(debounceTimer);
        }
    }, [session.userid, session.email]);

    // Transform data for display
    const questionnaireList = useMemo(() => Object.values(questionnaires), [questionnaires]);
    const attemptMap = useMemo(() => {
        const map: Record<string, Record<'listening' | 'reading' | 'writing', AttemptType | undefined>> = {};
        
        questionnaireList.forEach(q => {
            const questionnaireAttempts = Object.values(attempts).filter(a => a.examid === q.examid);
            map[q.questionnaireid] = {
                listening: questionnaireAttempts.find(a => a.section === 'listening'),
                reading: questionnaireAttempts.find(a => a.section === 'reading'),
                writing: questionnaireAttempts.find(a => a.section === 'writing')
            };
        });
        
        return map;
    }, [questionnaireList, attempts]);

    const loading = questionnairesLoading || attemptsLoading;
    const error = questionnairesError || attemptsError;

    const getSectionStatus = (questionnaireid: string, section: 'listening' | 'reading' | 'writing') => {
        const sectionAttempt = attemptMap[questionnaireid]?.[section];
        if (!sectionAttempt) return null;
        return sectionAttempt.status;
    };

    const handleSectionClick = async (questionnaire: QuestionnaireType, section: 'listening' | 'reading' | 'writing') => {
        try {
            const sectionAttempt = attemptMap[questionnaire.questionnaireid]?.[section];
            
            if (sectionAttempt && sectionAttempt.status === 'completed') {
                // View result
                navigateTo(`/topik/${questionnaire.questionnaireid}/${sectionAttempt.attemptid}/${section}/result`);
            } else if (sectionAttempt) {
                // Continue test
                navigateTo(`/topik/${questionnaire.questionnaireid}/${sectionAttempt.attemptid}/${section}`);
            } else {
                // Create new attempt for this section using Recoil
                const response = await createAttempt({
                    questionnaireid: questionnaire.questionnaireid,
                    section
                });
                
                if (!response || !response.attemptid) {
                    alert('Failed to create attempt. Please try again.');
                    return;
                }
                
                navigateTo(`/topik/${questionnaire.questionnaireid}/${response.attemptid}/${section}`);
            }
        } catch (error) {
            console.error('Error handling section click:', error);
            alert('Error starting test. Please try again.');
        }
    };

    if (loading) {
        return (
            <div className="TopikDashboard">
                <div className="Container">
                    <div className="LoadingView">
                        <div className="LoadingSpinner"></div>
                        <p>Loading questionnaires...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="TopikDashboard">
                <div className="Container">
                    <div className="ErrorView">
                        <h3>Error</h3>
                        <p>{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="TopikDashboard">
            <div className="Container">
                <header className="Header">
                    <h1>TOPIK Practice Tests</h1>
                    <p>Prepare for your TOPIK exam with our practice questionnaires</p>
                </header>

                {questionnaireList.length === 0 ? (
                    <div className="EmptyState">
                        <div className="EmptyContent">
                            <h3>No Questionnaires Available</h3>
                            <p>You don't have any questionnaires allocated yet.</p>
                            <p>Please contact support to get access to practice tests.</p>
                            <br/>
                            <button className="StartButton" onClick={() => navigateTo('/product/topik-250622')}>
                                {"Get TOPIK Practice Tests"}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="QuestionnaireList">
                        {questionnaireList.map((questionnaire) => (
                            <div key={questionnaire.questionnaireid} className="QuestionnaireCard">
                                <div className="CardHeader">
                                    <h3>TOPIK II Test <span className="QuestionnaireId">{questionnaire.questionnaireid.split('-')[0]}</span></h3>
                                </div>
                                <div className="CardContent">
                                    {(['listening', 'reading', 'writing'] as const).map(section => {
                                        const status = getSectionStatus(questionnaire.questionnaireid, section);
                                        const isCompleted = status === 'completed';
                                        const isInProgress = status === 'active';
                                        
                                        return (
                                            <button key={section} className="SectionInfo" onClick={() => handleSectionClick(questionnaire, section)}>
                                                <CheckCircle style={{width: 24, height: 24}} color={isCompleted ? "success": isInProgress ? "warning" : "disabled"}/>
                                                <span className="SectionName">{section.charAt(0).toUpperCase() + section.slice(1)}</span>
                                                <span>{"→"}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default TopikDashboard;